import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getApiClient } from 'api';
import {
  getMessagesConversation,
  putMessagesByIdBlock,
  type PutMessagesByIdBlockResponse,
  type MessagesConversationResponse,
  type MessagesConversationItem,
} from 'api/generated';
import type { MessageParamsType } from 'types/user';

export const fetchMessages = createAsyncThunk<MessagesConversationResponse, MessageParamsType>(
  'messages/conversation/fetchMessages',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getMessagesConversation({
        client: getApiClient(false),
        query: params,
        throwOnError: true,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const blockUser = createAsyncThunk<PutMessagesByIdBlockResponse, number>(
  'messages/blockUser',
  async (id, { rejectWithValue }) => {
    try {
      const response = await putMessagesByIdBlock({
        client: getApiClient(false),
        path: { id: Number(id) },
        throwOnError: true,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const messagesSlice = createSlice({
  name: 'messages',
  initialState: {
    error: false,
    loading: false,
    messages: [] as MessagesConversationItem[],
  },
  reducers: {
    messageSent: (state, action: PayloadAction<MessagesConversationItem>) => {
      state.messages.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMessages.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(fetchMessages.fulfilled, (state, action: PayloadAction<MessagesConversationResponse>) => {
      state.loading = false;
      state.messages = action.payload;
    });
    builder.addCase(fetchMessages.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(blockUser.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(blockUser.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(blockUser.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
  },
});

export const { messageSent } = messagesSlice.actions;
export const messagesReducer = messagesSlice.reducer;
